import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Form, Input, Space } from 'antd';
import { message } from '@/components/Message';
import { FormInstance } from 'antd/es/form';
import { useForm } from 'antd/es/form/Form';
import { ModalProps } from 'antd/es/modal';
import React, { useRef, useState } from 'react';
import api from '../../api';
import Modal from '@/components/Modal';
import { NumericKeyboardInput } from '@/components/NumericKeyboard';
import PosButton from '@/components/PosButton';
import { User, UserEndpoint } from '@/models/user';

export enum PointActionType {
  ADD = 1,
  MINUS,
}

const typeMap = {
  [PointActionType.ADD]: {
    text: '发放',
    factor: 1,
    api: 'plus',
  },
  [PointActionType.MINUS]: {
    text: '核销',
    factor: -1,
    api: 'minus',
  },
};

type PointFormProps = {
  user: User;
  actionType: PointActionType;
  form: FormInstance;
  onSuccess: (v: number) => void;
};

const PointForm: React.FC<PointFormProps> = ({ user, actionType, form, onSuccess }: PointFormProps) => {
  const isSubmitting = useRef(false);

  const changePoint = async (value: number, comment: string, user: User) => {
    if (isSubmitting.current) {
      message.warning('请不要重复提交');
      return;
    }
    const hideLoading = message.loading('处理中...');
    isSubmitting.current = true;

    try {
      const newPoint = await api.post<number>({
        url: `${UserEndpoint}/point/${typeMap[actionType].api}`,
        data: {
          userId: user.id,
          point: Math.abs(value),
          comment,
        },
      });
      message.success('操作成功，积分已变更');
      onSuccess(newPoint);
    } catch (e) {
      message.error(`操作失败: ${e.msg}`);
      isSubmitting.current = false;
    } finally {
      hideLoading();
    }
  };

  if (!actionType) return null;

  return (
    <Form
      form={form}
      onFinish={({ value, comment }) => {
        if (value) {
          changePoint(value, comment, user);
        } else {
          message.error('请输入有效的积分值');
        }
      }}
      layout={'vertical'}>
      <Alert
        className="mb-l"
        message={
          <>
            您当前正在进行积分<span className="emphasize red">{typeMap[actionType].text}</span>
            ，请谨慎操作
          </>
        }
        type="warning"
        showIcon
      />
      <Form.Item name="value" colon={false} label={`${typeMap[actionType].text}积分数`} required>
        <NumericKeyboardInput inputProps={{ min: 1 }} />
      </Form.Item>
      <Form.Item name="comment" colon={false} label={'操作说明 (用户可见)'}>
        <Input placeholder={''} />
      </Form.Item>
    </Form>
  );
};

type PointFormModalProps = ModalProps & Pick<PointFormProps, 'user' | 'onSuccess'>;

export const UserPointOperator = ({ user, onSuccess: onSuccessProp, ...modalProps }: PointFormModalProps) => {
  const [actionType, setActionType] = useState<PointActionType>();
  const [form] = useForm();

  const onSuccess = (newPoint: number) => {
    setActionType(null);
    onSuccessProp(newPoint);
    form.resetFields();
  };

  return (
    <>
      <Modal
        {...modalProps}
        title="积分操作"
        destroyOnHidden
        open={Boolean(actionType)}
        onOk={async () => {
          form.submit();
        }}
        onCancel={() => {
          setActionType(null);
          form.resetFields();
        }}>
        <PointForm form={form} actionType={actionType} user={user} onSuccess={onSuccess} />
      </Modal>
      <Space>
        <PosButton
          color="primary"
          variant="outlined"
          icon={<PlusOutlined />}
          onClick={() => {
            setActionType(PointActionType.ADD);
          }}>
          发放积分
        </PosButton>
        <PosButton
          color="primary"
          variant="outlined"
          icon={<MinusOutlined />}
          onClick={() => {
            setActionType(PointActionType.MINUS);
          }}>
          核销积分
        </PosButton>
      </Space>
    </>
  );
};
